import { Player, Signup, Course, TeeInfo, RoundGroup } from '../../types';

export const calculateCourseHandicap = (index: number | null, tee: TeeInfo | undefined) => {
  if (index === null || !tee) return null;
  const result = (index * (tee.slope / 113)) + (tee.rating - tee.par);
  return Math.round(result);
};

export const getGroupSizes = (n: number): number[] => {
  if (n === 0) return [];
  if (n === 1) return [1];
  if (n === 2) return [2];
  if (n === 3) return [3];
  if (n === 4) return [4];
  if (n === 5) return [3, 2];
  
  let f = Math.floor(n / 4);
  let r = n % 4;
  let t = 0;
  
  if (r === 0) { t = 0; }
  else if (r === 3) { t = 1; }
  else if (r === 2) { f -= 1; t = 2; }
  else if (r === 1) { f -= 2; t = 3; }
  
  const sizes: number[] = [];
  for (let i = 0; i < f; i++) sizes.push(4);
  for (let i = 0; i < t; i++) sizes.push(3);
  return sizes;
};

export const generateBalancedGroups = (
  signups: Signup[],
  players: Player[],
  course: Course | undefined
): RoundGroup[] => {
  // 1. Get active signups
  const activeSignups = signups.filter(s => s.status === 'in');
  if (activeSignups.length === 0) return [];

  // 2. Map to player data with CH
  const playerData = activeSignups.map(s => {
    const player = players.find(p => p.id === s.playerId);
    const tee = course?.tees.find((t: TeeInfo) => t.id === s.teeId);
    const ch = calculateCourseHandicap(player?.handicapIndex || null, tee);
    return {
      signup: s,
      player,
      ch: ch !== null ? ch : 18, // Treat null CH as 18 to avoid skewing averages
      hi: player?.handicapIndex !== null ? player!.handicapIndex : 18,
      name: player?.name || '',
      timestamp: s.updatedAt || 0
    };
  }).filter(d => d.player);

  // 3. Sort: CH (desc) -> HI (desc) -> Timestamp (asc) -> Name (asc)
  playerData.sort((a, b) => {
    if (a.ch !== b.ch) return b.ch - a.ch;
    if (a.hi !== b.hi) return b.hi - a.hi;
    if (a.timestamp !== b.timestamp) return a.timestamp - b.timestamp;
    return a.name.localeCompare(b.name);
  });

  // 4. Get group sizes
  const sizes = getGroupSizes(playerData.length);
  
  // 5. Initialize groups
  const groups = sizes.map((size, i) => ({
    id: `g${i + 1}`,
    name: `Group ${i + 1}`,
    players: [] as typeof playerData,
    capacity: size
  }));

  // 6. Greedy Distribution
  for (const data of playerData) {
    let bestGroup = null;
    let lowestAvg = Infinity;

    for (const group of groups) {
      if (group.players.length < group.capacity) {
        const currentSum = group.players.reduce((sum, p) => sum + p.ch, 0);
        const avg = currentSum / group.capacity;
        if (avg < lowestAvg) {
          lowestAvg = avg;
          bestGroup = group;
        }
      }
    }

    if (bestGroup) {
      bestGroup.players.push(data);
    }
  }

  // 7. Local Search (Hill Climbing) to optimize balance
  const getCost = () => {
    let totalCh = 0;
    let totalPlayers = 0;
    for (const g of groups) {
      for (const p of g.players) {
        totalCh += p.ch;
        totalPlayers++;
      }
    }
    const globalAvg = totalCh / totalPlayers;
    
    let cost = 0;
    for (const g of groups) {
      if (g.players.length > 0) {
        const gSum = g.players.reduce((sum, p) => sum + p.ch, 0);
        const gAvg = gSum / g.players.length;
        cost += Math.pow(gAvg - globalAvg, 2);
      }
    }
    return cost;
  };

  let currentCost = getCost();
  let improved = true;
  let iterations = 0;

  while (improved && iterations < 100) {
    improved = false;
    iterations++;

    for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        for (let p1 = 0; p1 < groups[i].players.length; p1++) {
          for (let p2 = 0; p2 < groups[j].players.length; p2++) {
            // Swap
            const temp = groups[i].players[p1];
            groups[i].players[p1] = groups[j].players[p2];
            groups[j].players[p2] = temp;

            const newCost = getCost();

            if (newCost < currentCost - 0.0001) { // small epsilon to prevent floating point loops
              currentCost = newCost;
              improved = true;
            } else {
              // Swap back
              const temp2 = groups[i].players[p1];
              groups[i].players[p1] = groups[j].players[p2];
              groups[j].players[p2] = temp2;
            }
          }
        }
      }
    }
  }

  // 8. Format output
  return groups.map(g => ({
    id: g.id,
    name: g.name,
    playerIds: g.players.map(p => p.player!.id)
  }));
};
